import React from "react";
import {
  formatDescription,
  FormattedPart,
} from "../../utils/formatDescription";

interface EventOverviewProps {
  eventId: string;
  title: string;
  description: string;
  date: string;
  time: string;
  address: string;
}

const EventOverview: React.FC<EventOverviewProps> = ({
  eventId,
  title,
  description,
  date,
  time,
  address,
}) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "Date to be announced";
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderFormattedDescription = (formattedParts: FormattedPart[]) => {
    return formattedParts.map((part, index) => {
      switch (part.type) {
        case "bold":
          return <strong key={index}>{part.content}</strong>;
        case "italic":
          return <em key={index}>{part.content}</em>;
        case "escaped":
        case "text":
          return <span key={index}>{part.content}</span>;
        case "linebreak":
          return <br key={index} />;
        default:
          return null;
      }
    });
  };

  const formattedDescription = description
    ? formatDescription(description)
    : [];

  return (
    <section className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-primary text-white p-6">
        <h2 className="text-2xl font-bold">
          {title || "No Event Currently Scheduled"}
        </h2>
      </div>
      <div className="p-6">
        {description ? (
          <div className="text-gray-700 leading-relaxed">
            {renderFormattedDescription(formattedDescription)}
          </div>
        ) : (
          <p className="text-gray-500">
            No event details available at this time. Please check back later for
            updates.
          </p>
        )}
        <div className="mt-4 space-y-2">
          <div className="flex items-center text-sm text-gray-500">
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
            {formatDate(date)}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clipRule="evenodd"
              />
            </svg>
            {time || "Time to be announced"}
          </div>
          <div className="flex items-start text-sm text-gray-500">
            <svg
              className="w-4 h-4 mr-2 mt-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            <span>{address || "Address to be announced"}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventOverview;
